import { BADGE_TYPE, TROPHY_TYPE, type AchievementAwardedMessage } from "@repo/shared";
import { cva } from "class-variance-authority";
import { camelCase } from "lodash-es";
import { useTranslation } from "react-i18next";

import LightningImage from "~/assets/images/lightning.webp";
import { Icon } from "~/components/Icon";
import { cn } from "~/lib/utils";
import { getSubjectImage } from "~/utils/getSubjectImage";

import type { VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";

const toastVariants = cva(
  "lg:details text-[10px] leading-[160%] sm:max-w-none sm:w-max flex max-w-[358px] w-full items-center gap-x-3 rounded-xl px-5 py-4 text-white",
  {
    variants: {
      variant: {
        1: "bg-[#333A63]",
        2: "bg-[#75216F]",
        3: "bg-[#0648AC]",
        4: "bg-[#4E11A1]",
      },
    },
    defaultVariants: {
      variant: 1,
    },
  },
);

type GamificationToastProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof toastVariants> & {
    badge: AchievementAwardedMessage["badge"];
    onDismiss: () => void;
    isShared?: boolean;
  };

export const GamificationToast = ({
  badge,
  onDismiss,
  className,
  onViewBadge,
  ...props
}: GamificationToastProps & { onViewBadge?: () => void }) => {
  const { t } = useTranslation();
  const { badgeType, trophyType, name, tier, schoolClassName, subjectCodes } = badge;

  const variant = 1 <= tier && tier <= 4 ? (tier as 1 | 2 | 3 | 4) : 1;

  return (
    <div className={cn(toastVariants({ variant }), className)} {...props}>
      <div className="flex w-full items-center gap-x-2">
        <div className="mr-1 flex shrink-0">
          {!!subjectCodes?.length && badgeType !== BADGE_TYPE.POWER_SIGN ? (
            <>
              {subjectCodes?.map((code) => {
                return <img key={code} src={getSubjectImage(code)} alt="" className="h-auto w-6" />;
              })}
            </>
          ) : (
            <img src={LightningImage} alt="" className="size-6" />
          )}
        </div>
        <p className="w-full max-w-none xl:w-max xl:shrink-0">
          {trophyType !== TROPHY_TYPE.STARTER &&
            t(`gamification.badgeType.${camelCase(badgeType)}`)}
          <span className="font-bold">
            {name}
            {!schoolClassName && trophyType !== TROPHY_TYPE.STARTER && ":"}{" "}
          </span>
          {schoolClassName && <span>({schoolClassName}): </span>}
          {tier && trophyType !== TROPHY_TYPE.STARTER && (
            <span className="font-bold">{t("gamification.level", { level: variant })}</span>
          )}
        </p>
        <button onClick={onViewBadge} className="w-max shrink-0 underline">
          {t("gamification.viewBadge")}
        </button>
      </div>
      <button onClick={onDismiss} aria-label="Zamknij powiadomienie">
        <Icon name="CROSS" className="size-5 text-white" />
      </button>
    </div>
  );
};
